import { useParams, useNavigate, Link } from "react-router";
import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import * as React from "react";
import {
  CalendarClock,
  CloudSun,
  FileDown,
  FileText,
  ListChecks,
  Pencil,
  Scale,
  Sun,
  Thermometer,
  ThermometerSun,
} from "lucide-react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { apiFetch, APIError } from "@/lib/api-client";
import { downloadAuthedFile } from "@/lib/download-file";
import { useAuth } from "@/features/auth/auth-context";
import { canCorrectEntry } from "@/features/auth/role-utils";
import { toast } from "@/components/ui/toaster";
import { HeatBadge } from "@/components/ui/heat-badge";
import { Modal } from "@/components/ui/modal";
import { Field } from "@/components/ui/field";
import { STUFEN_INFO, STUFEN_SELECT_OPTIONS } from "@/lib/stufen-labels";
import type { ConfirmedLevel } from "@/lib/stufen-labels";
import type {
  DashboardStatusSlim,
  EntriesByRoomResponse,
  Measure,
  MeasuresListResponse,
  Room,
  RoomEntry,
} from "./types";

// ─── Correction-chain helpers ─────────────────────────────────────────────────

/** POST /v1/entries/{id}/correct — request body (Item 3 Compliance-Batch). */
interface CorrectEntryBody {
  indoor_temp_c: number;
  confirmed_level?: 0 | 1 | 2 | 3;
  level_override_reason: string;
  measures_taken?: string[];
  measures_notes?: string;
}

const correctionSchema = z.object({
  indoor_temp_c: z.coerce
    .number("Bitte Temperatur eingeben")
    .min(-10, "Mindestens -10 °C")
    .max(60, "Höchstens 60 °C"),
  confirmed_level: z.union([z.literal(""), z.coerce.number().int().min(0).max(3)]),
  level_override_reason: z
    .string()
    .min(1, "Begründung ist Pflichtfeld (10-Jahre-Nachweis)")
    .max(500, "Maximal 500 Zeichen"),
  measures_notes: z.string().max(2000).optional(),
});

type CorrectionFormData = z.infer<typeof correctionSchema>;

/**
 * CorrectionModal — modal für korrektur eines versiegelten eintrags.
 *
 * POST /v1/entries/{id}/correct (license-gated mutation). Bei erfolg:
 * - listiert entries- und dashboard-queries neu (trail ist sofort sichtbar).
 * - server liefert 201 mit dem sofort-versiegelten korrektur-eintrag zurück.
 *
 * Nur an sealed einträgen (is_provisional=false) angeboten. Provisorische
 * same-day-einträge nutzen weiterhin den bestehenden edit-pfad (PATCH).
 */
function CorrectionModal({
  entry,
  open,
  onClose,
}: {
  entry: RoomEntry;
  open: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();

  const form = useForm<CorrectionFormData>({
    resolver: zodResolver(correctionSchema),
    defaultValues: {
      indoor_temp_c: entry.indoor_temp_c,
      confirmed_level: "",
      level_override_reason: "",
      measures_notes: entry.measures_notes ?? "",
    },
  });

  // Reset form when modal is opened for the given entry (e.g. different entry).
  React.useEffect(() => {
    if (open) {
      form.reset({
        indoor_temp_c: entry.indoor_temp_c,
        confirmed_level: "",
        level_override_reason: "",
        measures_notes: entry.measures_notes ?? "",
      });
    }
  }, [open, entry.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const correct = useMutation({
    mutationFn: async (data: CorrectionFormData) => {
      const body: CorrectEntryBody = {
        indoor_temp_c: Number(data.indoor_temp_c),
        level_override_reason: data.level_override_reason,
      };
      if (data.confirmed_level !== "" && data.confirmed_level !== undefined) {
        body.confirmed_level = Number(data.confirmed_level) as 0 | 1 | 2 | 3;
      }
      if (data.measures_notes) body.measures_notes = data.measures_notes;
      return apiFetch<RoomEntry>(`/v1/entries/${entry.id}/correct`, {
        method: "POST",
        body,
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["entries"] });
      void qc.invalidateQueries({ queryKey: ["dashboard"] });
      toast("Korrektur gespeichert und versiegelt", "success");
      onClose();
    },
    onError: (err) => {
      if (err instanceof APIError) {
        switch (err.code) {
          case "use_patch_for_editable":
            toast("Noch bearbeitbarer Eintrag — bitte normalen Bearbeitungs-Pfad nutzen", "warning");
            break;
          case "reason_required":
            toast("Begründung ist Pflicht", "warning");
            break;
          case "entry_not_found":
            toast("Eintrag nicht gefunden", "danger");
            break;
          default:
            toast(`Fehler: ${err.code}`, "danger");
        }
      } else {
        toast("Korrektur fehlgeschlagen", "danger");
      }
    },
  });

  const eventDate = dateTimeFormatter.format(new Date(entry.event_at));

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Messung korrigieren"
      description={`Versiegelter Eintrag vom ${eventDate} — Korrektur erzeugt einen neuen, sofort versiegelten Eintrag. Der ursprüngliche bleibt im Nachweis-Trail erhalten.`}
      busy={correct.isPending}
      size="md"
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            disabled={correct.isPending}
            className="h-11 rounded-md border border-[color:var(--color-border)] px-5 text-sm font-medium hover:bg-[color:var(--color-surface-sunken)] disabled:opacity-50"
          >
            Abbrechen
          </button>
          <button
            type="submit"
            form="correction-form"
            disabled={correct.isPending}
            className="h-11 rounded-md bg-[color:var(--color-brand-primary-strong)] px-5 text-sm font-semibold text-white disabled:opacity-50"
          >
            {correct.isPending ? "Korrektur speichern …" : "Korrektur speichern"}
          </button>
        </>
      }
    >
      <form
        id="correction-form"
        onSubmit={form.handleSubmit((d) => correct.mutate(d))}
        className="space-y-4"
        noValidate
      >
        <Field
          label="Innentemperatur (°C) *"
          error={form.formState.errors.indoor_temp_c?.message}
        >
          <input
            type="number"
            inputMode="decimal"
            step="0.1"
            min={-10}
            max={60}
            className="h-12 w-full rounded-md border border-[color:var(--color-border)] bg-white px-3 text-lg tabular-nums"
            {...form.register("indoor_temp_c")}
          />
        </Field>

        <Field
          label="Stufe (optional — leer = automatisch)"
          error={form.formState.errors.confirmed_level?.message}
        >
          <select
            className="h-12 w-full rounded-md border border-[color:var(--color-border)] bg-white px-3"
            {...form.register("confirmed_level")}
          >
            <option value="">Automatisch berechnen</option>
            {STUFEN_SELECT_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </Field>

        <Field
          label="Begründung der Korrektur *"
          error={form.formState.errors.level_override_reason?.message}
        >
          <input
            type="text"
            placeholder="z. B. Tippfehler Temperatur, falsche Stufe eingetragen"
            className="h-12 w-full rounded-md border border-[color:var(--color-border)] bg-white px-3"
            {...form.register("level_override_reason")}
          />
        </Field>

        <Field
          label="Notiz (optional)"
          error={form.formState.errors.measures_notes?.message}
        >
          <textarea
            rows={3}
            placeholder="Zusätzliche Anmerkungen zur Korrektur"
            className="w-full rounded-md border border-[color:var(--color-border)] bg-white px-3 py-2 text-sm"
            {...form.register("measures_notes")}
          />
        </Field>
      </form>
    </Modal>
  );
}

/**
 * RoomDetailRoute — vollständige raum-ansicht (Phase: raum-detail-ausbau).
 *
 * Ersetzt die alte "nur stufe + roh-liste"-ansicht. Zeigt analog alt-system:
 *   - aktueller eintrag prominent (suggested vs confirmed level, indoor/outdoor,
 *     mess-zeitpunkt, provisorisch-marker, gemessene maßnahmen mit labels, notiz)
 *   - aktuelle einrichtungs-außenbedingungen (außentemp, UV, DWD-status) — ORG-
 *     WEIT live aus /v1/dashboard/status.weather, klar als solche gelabelt
 *     (kein pro-eintrag-fake, weil der by-room-contract diese werte nicht liefert)
 *   - historie-drilldown: paginierte timeline aller einträge dieses raums
 *
 * Daten:
 *   - GET /v1/rooms                          → raum-stammdaten
 *   - GET /v1/entries/by-room/{id}?limit&offset → historie (voller entry-contract)
 *   - GET /v1/dashboard/status               → org-live-wetter (best-effort)
 *   - GET /v1/measures/{level} ×4            → measure_code → label-auflösung (best-effort)
 *
 * Die measure-code-auflösung ist graceful: schlägt sie fehl, werden codes roh
 * angezeigt statt die ansicht zu blockieren.
 */

const PAGE_SIZE = 25;
const STALE_THRESHOLD_MS = 24 * 60 * 60 * 1000;

// Backend-konvention: 0..3-smallint = normal/soll/muss/kritisch.
// Labels + Farb-Tokens aus single-source stufen-labels.ts (B3).
const LEVELS = ["normal", "soll", "muss", "kritisch"] as const;
type LevelSlug = (typeof LEVELS)[number];

/** Klemmt beliebige number auf ConfirmedLevel 0..3. */
function clampToLevel(level: number): ConfirmedLevel {
  if (level <= 0) return 0;
  if (level >= 3) return 3;
  return level as ConfirmedLevel;
}

const tempFormatter = new Intl.NumberFormat("de-DE", {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});
function formatTemp(value: number): string {
  return `${tempFormatter.format(value)} °C`;
}

const uvFormatter = new Intl.NumberFormat("de-DE", {
  minimumFractionDigits: 0,
  maximumFractionDigits: 1,
});
function formatUV(value: number): string {
  return uvFormatter.format(value);
}

const dateTimeFormatter = new Intl.DateTimeFormat("de-DE", {
  dateStyle: "medium",
  timeStyle: "short",
});

export function RoomDetailRoute() {
  const { id } = useParams<{ id: string }>();
  const auth = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  // canWrite = admin | leitung: raum-löschen + entry-löschen (unveraendert).
  const canWrite = auth.user?.role === "admin" || auth.user?.role === "leitung";

  // Correction modal state — one modal shared, controlled by entry-id.
  const [correctionEntry, setCorrectionEntry] = React.useState<RoomEntry | null>(null);

  const rooms = useQuery({
    queryKey: ["rooms", "list"],
    queryFn: () => apiFetch<{ rooms: Room[] }>("/v1/rooms"),
  });
  const room = rooms.data?.rooms.find((r) => r.id === id);

  const [limit, setLimit] = React.useState(PAGE_SIZE);
  const entries = useQuery({
    queryKey: ["entries", "by-room", id, limit],
    queryFn: () =>
      apiFetch<EntriesByRoomResponse>(`/v1/entries/by-room/${id}?limit=${limit}`),
    enabled: !!id,
    placeholderData: (prev) => prev,
  });

  // Org-weiter live-wetter-snapshot. Best-effort, blockt die ansicht nie —
  // bei fehler bleibt der außenbedingungs-block schlicht aus.
  const status = useQuery({
    queryKey: ["dashboard", "status"],
    queryFn: () => apiFetch<DashboardStatusSlim>("/v1/dashboard/status"),
    staleTime: 5 * 60 * 1000,
  });

  const measureLabels = useMeasureLabels();

  const softDelete = useMutation({
    mutationFn: async () =>
      apiFetch(`/v1/rooms/${id}`, {
        method: "DELETE",
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["rooms"] });
      void qc.invalidateQueries({ queryKey: ["dashboard"] });
      toast("Raum entfernt", "success");
      navigate("/raeume", { replace: true });
    },
    onError: () => toast("Löschen fehlgeschlagen", "danger"),
  });

  const [confirmDelete, setConfirmDelete] = React.useState(false);

  // Protokoll-PDF download state
  const [pdfDownloading, setPdfDownloading] = React.useState(false);
  const [pdfError, setPdfError] = React.useState<string | null>(null);

  async function handlePdfDownload() {
    if (!id) return;
    setPdfDownloading(true);
    setPdfError(null);
    try {
      await downloadAuthedFile(`/v1/rooms/${id}/export.pdf?period=all`, {
        fallbackName: "raum-protokoll.pdf",
      });
    } catch (e) {
      setPdfError(e instanceof Error ? e.message : "Download fehlgeschlagen");
    }
    setPdfDownloading(false);
  }

  if (rooms.isPending || entries.isPending) {
    return <p className="text-sm text-neutral-500">Lade …</p>;
  }
  if (!room) {
    return (
      <section className="space-y-4">
        <p>Raum nicht gefunden.</p>
        <Link to="/raeume" className="text-[color:var(--color-brand-accent)] underline">
          Zurück zur Übersicht
        </Link>
      </section>
    );
  }

  const allEntries = entries.data?.entries ?? [];
  const latest = allEntries[0];
  const history = allEntries.slice(1);
  const hasMore = entries.data ? entries.data.count > limit : false;
  const isStale = latest
    ? Date.now() - Date.parse(latest.event_at) > STALE_THRESHOLD_MS
    : false;

  return (
    <section className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <Link
            to="/raeume"
            className="text-sm text-[color:var(--color-brand-accent)] underline"
          >
            ← Räume
          </Link>
          <h1 className="mt-1 text-3xl font-semibold">{room.name}</h1>
          <p className="text-sm text-neutral-500">
            {[room.etage, room.kapazitaet && `${room.kapazitaet} Plätze`]
              .filter(Boolean)
              .join(" · ")}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Link
            to={`/entries/neu?room=${room.id}`}
            className="inline-flex h-12 items-center gap-2 rounded-md bg-[color:var(--color-brand-primary-strong)] px-4 font-semibold text-white"
          >
            <Thermometer aria-hidden="true" className="size-4" />
            Neue Messung
          </Link>
          <button
            type="button"
            onClick={() => void handlePdfDownload()}
            disabled={pdfDownloading}
            aria-busy={pdfDownloading}
            className="inline-flex h-12 items-center gap-2 rounded-md border border-[color:var(--color-border)] bg-white px-4 text-sm font-medium text-[color:var(--color-text-primary)] disabled:opacity-60"
          >
            <FileDown aria-hidden="true" className="size-4" />
            {pdfDownloading ? "Wird geladen …" : "Protokoll-PDF"}
          </button>
          {canWrite && (
            <button
              type="button"
              onClick={() => setConfirmDelete(true)}
              className="h-12 rounded-md border border-[color:var(--color-heat-4)] bg-white px-4 text-sm font-medium text-[color:var(--color-heat-4)]"
            >
              Raum entfernen
            </button>
          )}
        </div>
      </header>
      {pdfError && (
        <p role="alert" className="text-sm text-[color:var(--color-heat-4)]">
          {pdfError}
        </p>
      )}

      {room.notiz && (
        <article className="rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-surface-elevated)] p-4 text-sm shadow-[var(--shadow-sm)]">
          <p className="font-medium text-[color:var(--color-text-primary)]">Raum-Notiz</p>
          <p className="mt-1 text-[color:var(--color-text-secondary)]">{room.notiz}</p>
        </article>
      )}

      {confirmDelete && (
        <article className="rounded-lg border-2 border-[color:var(--color-heat-4)] bg-white p-4">
          <p className="text-sm">
            Raum wirklich entfernen? Bestehende Messungen bleiben erhalten und sind weiterhin
            über Exporte abrufbar.
          </p>
          <div className="mt-3 flex gap-2">
            <button
              type="button"
              onClick={() => softDelete.mutate()}
              disabled={softDelete.isPending}
              className="rounded-md bg-[color:var(--color-heat-4)] px-4 py-2 text-sm font-semibold text-white"
            >
              {softDelete.isPending ? "Wird entfernt …" : "Ja, entfernen"}
            </button>
            <button
              type="button"
              onClick={() => setConfirmDelete(false)}
              className="rounded-md border border-[color:var(--color-border)] px-4 py-2 text-sm"
            >
              Abbrechen
            </button>
          </div>
        </article>
      )}

      {latest ? (
        <CurrentEntryCard
          entry={latest}
          weather={status.data?.weather}
          measureLabels={measureLabels}
          isStale={isStale}
          canCorrect={canCorrectEntry(auth.user, latest)}
          onCorrect={() => setCorrectionEntry(latest)}
        />
      ) : (
        <article className="rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-surface-elevated)] p-6 text-center text-sm text-[color:var(--color-text-secondary)] shadow-[var(--shadow-sm)]">
          Für diesen Raum wurde noch keine Messung dokumentiert.{" "}
          <Link
            to={`/entries/neu?room=${room.id}`}
            className="font-medium text-[color:var(--color-brand-accent)] underline"
          >
            Erste Messung erfassen
          </Link>
          .
        </article>
      )}

      <article>
        <header className="mb-3 flex items-center justify-between gap-2">
          <h2 className="text-xl font-semibold">Verlauf</h2>
          {entries.data && (
            <span className="text-xs text-[color:var(--color-text-secondary)]">
              {entries.data.count}{" "}
              {entries.data.count === 1 ? "Eintrag" : "Einträge"}
            </span>
          )}
        </header>
        {history.length === 0 ? (
          <p className="rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-surface-elevated)] p-4 text-sm text-[color:var(--color-text-secondary)]">
            {latest
              ? "Keine weiteren Einträge — die aktuelle Messung ist die einzige."
              : "Noch keine Messungen im Verlauf."}
          </p>
        ) : (
          <ol className="space-y-2">
            {history.map((e) => (
              <HistoryItem
                key={e.id}
                entry={e}
                measureLabels={measureLabels}
                canDelete={canWrite}
                canCorrect={canCorrectEntry(auth.user, e)}
                onCorrect={() => setCorrectionEntry(e)}
              />
            ))}
          </ol>
        )}
        {hasMore && (
          <div className="mt-3">
            <button
              type="button"
              onClick={() => setLimit((l) => l + PAGE_SIZE)}
              disabled={entries.isFetching}
              className="h-11 w-full rounded-md border border-[color:var(--color-border)] text-sm font-medium hover:bg-[color:var(--color-surface-sunken)] disabled:opacity-50"
            >
              {entries.isFetching ? "Lade …" : "Ältere Einträge laden"}
            </button>
          </div>
        )}
      </article>

      {correctionEntry && (
        <CorrectionModal
          entry={correctionEntry}
          open={correctionEntry !== null}
          onClose={() => setCorrectionEntry(null)}
        />
      )}
    </section>
  );
}

/**
 * useMeasureLabels — lädt alle 4 level-kataloge parallel und baut eine
 * code → measure-map. Best-effort: failende level-queries werden ignoriert,
 * die map enthält dann nur die erfolgreich geladenen codes. Liefert auch
 * `ready=false` solange noch geladen wird, damit die UI keinen flash von
 * roh-codes → labels zeigt.
 */
function useMeasureLabels(): { map: Map<string, Measure>; ready: boolean } {
  const results = useQueries({
    queries: LEVELS.map((level: LevelSlug) => ({
      queryKey: ["measures", level],
      queryFn: () => apiFetch<MeasuresListResponse>(`/v1/measures/${level}`),
      staleTime: 60 * 60 * 1000, // catalog ist quasi statisch
      retry: 1,
    })),
  });

  return React.useMemo(() => {
    const map = new Map<string, Measure>();
    for (const r of results) {
      if (r.data) {
        for (const m of r.data.measures) {
          map.set(m.code, m);
        }
      }
    }
    const ready = results.every((r) => !r.isPending);
    return { map, ready };
  }, [results]);
}

function CurrentEntryCard({
  entry,
  weather,
  measureLabels,
  isStale,
  canCorrect,
  onCorrect,
}: {
  entry: RoomEntry;
  weather?: DashboardStatusSlim["weather"];
  measureLabels: { map: Map<string, Measure>; ready: boolean };
  isStale: boolean;
  canCorrect: boolean;
  onCorrect: () => void;
}) {
  const overridden = entry.confirmed_level !== entry.suggested_level;
  // Sealed = finalized, non-provisional. Correction CTA nur hier.
  const isSealed = !entry.is_provisional;
  // is_superseded: dieser eintrag wurde durch eine neuere korrektur abgelöst.
  const isSuperseded = entry.is_superseded === true;

  return (
    <article
      aria-labelledby="aktueller-stand"
      className={
        isSuperseded
          ? "rounded-2xl border border-[color:var(--color-neutral-300)] bg-[color:var(--color-surface-elevated)] p-6 opacity-60 shadow-[var(--shadow-sm)]"
          : "rounded-2xl border border-[color:var(--color-border)] bg-[color:var(--color-surface)] p-6 shadow-[var(--shadow-md)]"
      }
    >
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2
            id="aktueller-stand"
            className="text-xs font-semibold uppercase tracking-[var(--letter-spacing-wide)] text-[color:var(--color-text-secondary)]"
          >
            Aktueller Stand
          </h2>
          <p className="mt-2 inline-flex items-center gap-1.5 text-sm text-[color:var(--color-text-secondary)]">
            <CalendarClock aria-hidden="true" strokeWidth={2} className="size-4" />
            {dateTimeFormatter.format(new Date(entry.event_at))} Uhr
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <HeatBadge
            level={clampToLevel(entry.confirmed_level)}
            temp={entry.indoor_temp_c}
            className={isSuperseded ? "opacity-60 grayscale" : undefined}
          />
          {entry.is_provisional && (
            <span className="rounded-full bg-[color:var(--color-heat-2-soft)] px-2.5 py-1 text-xs font-medium text-[color:var(--color-text-primary)]">
              vorläufig
            </span>
          )}
          {/* Correction-chain badges — cool-gray, niemals heat-orange (spec) */}
          {isSuperseded && (
            <span className="rounded-full bg-[color:var(--color-neutral-200)] px-2.5 py-1 text-xs font-medium text-[color:var(--color-neutral-600)]">
              korrigiert
            </span>
          )}
          {entry.corrects_entry_id && (
            <span className="rounded-full bg-[color:var(--color-neutral-200)] px-2.5 py-1 text-xs font-medium text-[color:var(--color-neutral-600)]">
              Korrektur
            </span>
          )}
          {isStale && !isSuperseded && (
            <span className="rounded-full bg-[color:var(--color-surface-sunken)] px-2.5 py-1 text-xs font-medium text-[color:var(--color-text-secondary)]">
              älter als 24 h
            </span>
          )}
          <DwdWarningBadge entry={entry} />
          {/* Correction CTA — nur an sealed, nicht-superseded einträgen */}
          {canCorrect && isSealed && !isSuperseded && (
            <button
              type="button"
              onClick={onCorrect}
              aria-label={`Messung vom ${dateTimeFormatter.format(new Date(entry.event_at))} korrigieren`}
              className="inline-flex items-center gap-1.5 rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-surface)] px-2.5 py-1 text-xs font-medium text-[color:var(--color-text-secondary)] hover:bg-[color:var(--color-surface-sunken)]"
            >
              <Pencil aria-hidden="true" strokeWidth={2} className="size-3.5" />
              Korrigieren
            </button>
          )}
        </div>
      </header>

      {/* Supersession-info — verweis auf die korrigierende entry */}
      {isSuperseded && entry.corrected_by_entry_id && (
        <p className="mt-3 rounded-md bg-[color:var(--color-neutral-100)] px-3 py-2 text-xs text-[color:var(--color-neutral-600)]">
          Dieser Eintrag wurde durch eine Korrektur abgelöst (Korrektur-ID:{" "}
          <span className="font-mono">{entry.corrected_by_entry_id.slice(0, 8)}&hellip;</span>
          ).
        </p>
      )}

      {/* Korrektur-herkunft — subtext wenn dieser eintrag eine korrektur ist */}
      {entry.corrects_entry_id && (
        <p className="mt-3 rounded-md bg-[color:var(--color-neutral-100)] px-3 py-2 text-xs text-[color:var(--color-neutral-600)]">
          Korrektur zu Eintrag vom{" "}
          <span className="font-medium">{dateTimeFormatter.format(new Date(entry.event_at))}</span>
          {entry.level_override_reason && (
            <>
              {" "}
              · Begründung: <em>{entry.level_override_reason}</em>
            </>
          )}
        </p>
      )}

      {/* Stufen-herleitung: suggested (server) vs confirmed (ggf. override) */}
      {overridden && !entry.corrects_entry_id && (
        <p className="mt-3 rounded-md bg-[color:var(--color-surface-elevated)] px-3 py-2 text-xs text-[color:var(--color-text-secondary)]">
          Server-Vorschlag war{" "}
          <strong className="text-[color:var(--color-text-primary)]">
            {STUFEN_INFO[clampToLevel(entry.suggested_level)]?.short ?? `Stufe ${entry.suggested_level}`}
          </strong>{" "}
          (DB-Wert {entry.suggested_level}), manuell bestätigt als{" "}
          <strong className="text-[color:var(--color-text-primary)]">
            {STUFEN_INFO[clampToLevel(entry.confirmed_level)]?.short ?? `Stufe ${entry.confirmed_level}`}
          </strong>
          .
          {entry.level_override_reason && (
            <>
              {" "}
              Begründung: <em>{entry.level_override_reason}</em>
            </>
          )}
        </p>
      )}

      <dl className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-4">
        <ValueTile
          icon={ThermometerSun}
          label="Innentemperatur"
          value={formatTemp(entry.indoor_temp_c)}
        />
        <ValueTile
          icon={Thermometer}
          label="Außentemp. (Messung)"
          value={entry.outdoor_temp_c !== undefined ? formatTemp(entry.outdoor_temp_c) : "—"}
        />
        <ValueTile
          icon={Sun}
          label="UV-Index (Messung)"
          value={entry.uv_index !== undefined ? formatUV(entry.uv_index) : "—"}
        />
        <ValueTile
          icon={ListChecks}
          label="Stufe"
          value={`${entry.confirmed_level} — ${STUFEN_INFO[clampToLevel(entry.confirmed_level)]?.short ?? "?"}`}
        />
      </dl>
      {/* Provenance der ZUR MESS-ZEIT erfassten außenwerte — getrennt vom
          org-live-snapshot unten (OutdoorConditions). */}
      {entry.api_fetched_at && (
        <p className="mt-2 inline-flex items-center gap-1.5 text-xs text-[color:var(--color-text-muted)]">
          <CloudSun aria-hidden="true" strokeWidth={2} className="size-3.5" />
          Wetter zur Messung erfasst um{" "}
          {dateTimeFormatter.format(new Date(entry.api_fetched_at))} Uhr
          {entry.api_source && entry.api_source !== "none" && (
            <> · Quelle {apiSourceLabel(entry.api_source)}</>
          )}
        </p>
      )}

      <MeasuresBlock entry={entry} measureLabels={measureLabels} />

      {entry.measures_notes && (
        <div className="mt-4">
          <p className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[var(--letter-spacing-wide)] text-[color:var(--color-text-secondary)]">
            <FileText aria-hidden="true" strokeWidth={2} className="size-3.5" />
            Notiz
          </p>
          <p className="mt-1 whitespace-pre-line rounded-md bg-[color:var(--color-surface-elevated)] p-3 text-sm text-[color:var(--color-text-primary)]">
            {entry.measures_notes}
          </p>
        </div>
      )}

      {weather && !isSuperseded && <OutdoorConditions weather={weather} />}
    </article>
  );
}

/**
 * OutdoorConditions — org-weite live-außenbedingungen (DWD/Open-Meteo).
 * BEWUSST getrennt vom eintrag dargestellt und gelabelt: diese werte sind
 * der aktuelle einrichtungs-snapshot, NICHT die zum mess-zeitpunkt erfassten
 * werte (die liefert der by-room-contract nicht — siehe types.ts GAP-hinweis).
 */
function OutdoorConditions({
  weather,
}: {
  weather: NonNullable<DashboardStatusSlim["weather"]>;
}) {
  const uvKnown = weather.uv_index >= 0;
  return (
    <section className="mt-5 border-t border-[color:var(--color-border)] pt-4">
      <p className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[var(--letter-spacing-wide)] text-[color:var(--color-text-secondary)]">
        <CloudSun aria-hidden="true" strokeWidth={2} className="size-3.5" />
        Außenbedingungen (aktuell, einrichtungsweit)
      </p>
      <dl className="mt-3 grid grid-cols-2 gap-4 sm:grid-cols-3">
        <ValueTile
          icon={Thermometer}
          label="Außentemperatur"
          value={formatTemp(weather.temperature_c)}
        />
        <ValueTile
          icon={Sun}
          label="UV-Index"
          value={
            uvKnown
              ? weather.uv_index.toLocaleString("de-DE", {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 1,
                })
              : "—"
          }
        />
        <ValueTile
          icon={CloudSun}
          label="DWD-Bewertung"
          value={dwdStatusLabel(weather.heat_level)}
        />
      </dl>
      <p className="mt-3 text-xs text-[color:var(--color-text-muted)]">
        Quelle:{" "}
        {weather.fallback ? "Open-Meteo (CC-BY 4.0)" : "DWD"} · Stand{" "}
        {dateTimeFormatter.format(new Date(weather.observed_at))} Uhr
      </p>
    </section>
  );
}

function dwdStatusLabel(level: "normal" | "soll" | "muss" | "kritisch"): string {
  switch (level) {
    case "normal":
      return "Normal";
    case "soll":
      return "Soll-Stufe";
    case "muss":
      return "Muss-Stufe";
    case "kritisch":
      return "Hitzewarnung";
  }
}

/** Rohe weather.Source-strings (dwd/openmeteo/cache) → lesbares label. */
function apiSourceLabel(source: string): string {
  switch (source) {
    case "dwd":
      return "DWD";
    case "openmeteo":
      return "Open-Meteo";
    case "cache":
      return "Cache";
    default:
      return source;
  }
}

/**
 * DwdWarningBadge — zeigt die ZUR MESS-ZEIT erfasste DWD-warnung des eintrags.
 * dwd_warning_code === 0 (oder undefined bei älterem server) = keine warnung
 * → nichts rendern. Bis zum MOSMIX-warncode-parser bleibt der code vorerst 0,
 * der badge ist also latentes plumbing (additiv, kein visueller noise heute).
 */
function DwdWarningBadge({
  entry,
  compact = false,
}: {
  entry: RoomEntry;
  compact?: boolean;
}) {
  if (!entry.dwd_warning_code) return null;
  const label = entry.dwd_warning_text?.trim() || "DWD-Warnung";
  return (
    <span
      title={`DWD-Warncode ${entry.dwd_warning_code}`}
      className={
        compact
          ? "rounded bg-[color:var(--color-heat-3-soft)] px-2 py-0.5 text-xs text-[color:var(--color-text-primary)]"
          : "rounded-full bg-[color:var(--color-heat-3-soft)] px-2.5 py-1 text-xs font-medium text-[color:var(--color-text-primary)]"
      }
    >
      {label}
    </span>
  );
}

function ValueTile({
  icon: Icon,
  label,
  value,
}: {
  icon: typeof Thermometer;
  label: string;
  value: string;
}) {
  return (
    <div>
      <Icon
        aria-hidden="true"
        strokeWidth={2}
        className="size-4 text-[color:var(--color-text-secondary)]"
      />
      <dt className="mt-1.5 text-xs font-medium text-[color:var(--color-text-secondary)]">
        {label}
      </dt>
      <dd className="mt-0.5 text-lg font-semibold tabular-nums text-[color:var(--color-text-primary)]">
        {value}
      </dd>
    </div>
  );
}

/**
 * MeasuresBlock — rendert die gemessenen maßnahmen-codes als chips mit
 * aufgelöstem titel (+ legal-reference im title-attribut). Solange der
 * katalog lädt: dezenter "lade"-hinweis statt roh-codes. Nicht-auflösbare
 * codes (katalog-fehler oder unbekannter code) fallen auf den rohen code
 * zurück, damit nie information verloren geht.
 */
function MeasuresBlock({
  entry,
  measureLabels,
}: {
  entry: RoomEntry;
  measureLabels: { map: Map<string, Measure>; ready: boolean };
}) {
  if (entry.measures_taken.length === 0) {
    return (
      <div className="mt-4">
        <p className="text-xs font-semibold uppercase tracking-[var(--letter-spacing-wide)] text-[color:var(--color-text-secondary)]">
          Maßnahmen
        </p>
        <p className="mt-1 text-sm text-[color:var(--color-text-muted)]">
          Keine Maßnahmen dokumentiert.
        </p>
      </div>
    );
  }
  return (
    <div className="mt-4">
      <p className="text-xs font-semibold uppercase tracking-[var(--letter-spacing-wide)] text-[color:var(--color-text-secondary)]">
        Maßnahmen ({entry.measures_taken.length})
      </p>
      <ul className="mt-2 flex flex-wrap gap-2">
        {entry.measures_taken.map((code) => {
          const m = measureLabels.map.get(code);
          const resolving = !measureLabels.ready && !m;
          return (
            <li key={code}>
              <span
                title={m?.legal_reference ?? undefined}
                className="inline-flex items-center gap-1.5 rounded-full border border-[color:var(--color-border)] bg-[color:var(--color-surface-elevated)] px-3 py-1 text-sm text-[color:var(--color-text-primary)]"
              >
                {m?.priority === "pflicht" && (
                  <Scale
                    aria-label="Pflicht-Maßnahme"
                    strokeWidth={2}
                    className="size-3.5 text-[color:var(--color-heat-3)]"
                  />
                )}
                <span className={resolving ? "text-[color:var(--color-text-muted)]" : ""}>
                  {m ? m.title : code}
                </span>
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function HistoryItem({
  entry,
  measureLabels,
  canDelete,
  canCorrect,
  onCorrect,
}: {
  entry: RoomEntry;
  measureLabels: { map: Map<string, Measure>; ready: boolean };
  canDelete: boolean;
  canCorrect: boolean;
  onCorrect: () => void;
}) {
  const qc = useQueryClient();
  const isSameDay = (() => {
    const a = new Date(entry.event_at);
    const b = new Date();
    return (
      a.getFullYear() === b.getFullYear() &&
      a.getMonth() === b.getMonth() &&
      a.getDate() === b.getDate()
    );
  })();

  const del = useMutation({
    mutationFn: async () =>
      apiFetch(`/v1/entries/${entry.id}`, {
        method: "DELETE",
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["entries"] });
      void qc.invalidateQueries({ queryKey: ["dashboard"] });
      toast("Messung gelöscht", "success");
    },
    onError: (err) => {
      if (err instanceof APIError && err.code === "entry_finalized") {
        toast("Bereits finalisierte Messung kann nicht gelöscht werden", "warning");
      } else if (err instanceof APIError && err.code === "entry_stale") {
        toast("Nur Messungen vom heutigen Tag sind bearbeitbar", "warning");
      } else {
        toast("Löschen fehlgeschlagen", "danger");
      }
    },
  });

  const overridden = entry.confirmed_level !== entry.suggested_level;
  const isSealed = !entry.is_provisional;
  const isSuperseded = entry.is_superseded === true;
  const isCorrection = Boolean(entry.corrects_entry_id);

  const measureSummary = entry.measures_taken.map((code) => {
    return measureLabels.map.get(code)?.title ?? code;
  });

  return (
    <li
      className={
        isSuperseded
          ? "rounded-lg border border-[color:var(--color-neutral-200)] bg-[color:var(--color-surface-elevated)] p-3 opacity-60"
          : "rounded-lg border border-[color:var(--color-border)] bg-white p-3"
      }
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <HeatBadge
              level={clampToLevel(entry.confirmed_level)}
              temp={entry.indoor_temp_c}
              compact
              className={isSuperseded ? "opacity-60 grayscale" : undefined}
            />
            {entry.is_provisional && (
              <span className="rounded bg-[color:var(--color-heat-2-soft)] px-2 py-0.5 text-xs">
                vorläufig
              </span>
            )}
            {/* Correction-chain badges — cool-gray (spec: NICHT heat-orange) */}
            {isSuperseded && (
              <span className="rounded bg-[color:var(--color-neutral-200)] px-2 py-0.5 text-xs text-[color:var(--color-neutral-600)]">
                korrigiert
              </span>
            )}
            {isCorrection && (
              <span className="rounded bg-[color:var(--color-neutral-200)] px-2 py-0.5 text-xs text-[color:var(--color-neutral-600)]">
                Korrektur
              </span>
            )}
            {overridden && !isCorrection && (
              <span
                className="rounded bg-[color:var(--color-surface-sunken)] px-2 py-0.5 text-xs text-[color:var(--color-text-secondary)]"
                title={
                  entry.level_override_reason
                    ? `Begründung: ${entry.level_override_reason}`
                    : `Server-Vorschlag: ${STUFEN_INFO[clampToLevel(entry.suggested_level)]?.short ?? entry.suggested_level} (${entry.suggested_level})`
                }
              >
                manuell gesetzt
              </span>
            )}
            <DwdWarningBadge entry={entry} compact />
          </div>
          <p className="mt-1.5 inline-flex items-center gap-1.5 text-xs text-[color:var(--color-text-secondary)]">
            <CalendarClock aria-hidden="true" strokeWidth={2} className="size-3.5" />
            {dateTimeFormatter.format(new Date(entry.event_at))} Uhr
            {entry.outdoor_temp_c !== undefined && (
              <>
                <span aria-hidden="true" className="opacity-50">
                  ·
                </span>
                außen {formatTemp(entry.outdoor_temp_c)}
              </>
            )}
            {entry.uv_index !== undefined && (
              <>
                <span aria-hidden="true" className="opacity-50">
                  ·
                </span>
                UV {formatUV(entry.uv_index)}
              </>
            )}
          </p>
          {/* Korrektur-subtext */}
          {isCorrection && (
            <p className="mt-1 text-xs text-[color:var(--color-neutral-600)]">
              Korrektur zu Eintrag vom{" "}
              <span className="font-medium">{dateTimeFormatter.format(new Date(entry.event_at))}</span>
              {entry.level_override_reason && (
                <> · <em>{entry.level_override_reason}</em></>
              )}
            </p>
          )}
          {measureSummary.length > 0 && (
            <p className="mt-1.5 text-sm text-[color:var(--color-text-primary)]">
              <span className="text-[color:var(--color-text-secondary)]">Maßnahmen: </span>
              {measureSummary.join(", ")}
            </p>
          )}
          {entry.measures_notes && (
            <p className="mt-1 whitespace-pre-line text-sm italic text-[color:var(--color-text-secondary)]">
              {entry.measures_notes}
            </p>
          )}
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1.5">
          {/* Delete CTA — nur provisorische same-day einträge */}
          {canDelete && isSameDay && entry.is_provisional && (
            <button
              type="button"
              onClick={() => del.mutate()}
              disabled={del.isPending}
              className="text-xs text-[color:var(--color-heat-4)] underline"
            >
              Löschen
            </button>
          )}
          {/* Correction CTA — nur sealed, nicht-superseded, nicht bereits eine korrektur */}
          {canCorrect && isSealed && !isSuperseded && (
            <button
              type="button"
              onClick={onCorrect}
              aria-label={`Messung vom ${dateTimeFormatter.format(new Date(entry.event_at))} korrigieren`}
              className="inline-flex items-center gap-1 text-xs text-[color:var(--color-text-secondary)] underline"
            >
              <Pencil aria-hidden="true" strokeWidth={2} className="size-3" />
              Korrigieren
            </button>
          )}
        </div>
      </div>
    </li>
  );
}
